import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { api, ApiError, setApiConfig } from './api/client';
import type { RuntimeConfig } from './auth/oauth';
import './styles.css';

/**
 * Fetches the runtime configuration before rendering anything.
 *
 * The bundle deliberately contains no client ID: the console's own app client is created by the CLI
 * after this stack deploys, so baking it in at build time would mean rebuilding the SPA to finish
 * the deployment — and again if the client were ever deleted and re-added.
 */
const root = createRoot(document.getElementById('root')!);

function fatal(title: string, detail: string, hint?: string) {
  root.render(
    <div className="centered">
      <h1>{title}</h1>
      <p>{detail}</p>
      {hint && <pre>{hint}</pre>}
    </div>,
  );
}

api
  .config()
  .then((config: RuntimeConfig) => {
    setApiConfig(config);
    root.render(
      <StrictMode>
        <App config={config} />
      </StrictMode>,
    );
  })
  .catch((err) => {
    // The expected first-deploy state: the stack is up but nobody has registered the console as an
    // app client yet. The API returns the exact command in its message, so render it verbatim
    // rather than showing a blank screen.
    if (err instanceof ApiError && err.status === 503) {
      fatal('The admin console is not registered yet', 'Run this once, then reload:', err.message);
      return;
    }
    fatal('Could not load the admin console', err instanceof Error ? err.message : String(err));
  });
